import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  useTheme
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { Stack } from "@mui/system";
import { Duplicate20Px, useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { FcGridInfo } from "~/components/serviceSupport/FcGridInfo";
import { SettingsSectionWrapper } from "~/components/settings/SettingsSection";
import envConstants from "~/config/envConstants";

import useFlag from "~/config/flags";
import { FulfillmentCenterSelect } from "~/features/preview/components/FulfillmentCenterSelect";
import {
  settingsPagePaddingSx,
  settingsSectionGap
} from "~/features/settings/settings.styles";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { copyToClipboard } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectUserIsAdmin,
  selectUserIsWorkstationConfigurer,
  selectUsersProfileName
} from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useGetAutostoreGridQuery,
  useGetAutostoreGridsQuery
} from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetServerVersionQuery } from "~/redux/warehouse/version";

import { GridSection } from "./components/GridSection";
import { LanguageSettings } from "./components/LanguageSettings";
import { WorkstationPanel } from "./components/WorkstationPanel";
import { useSettingsBeamerSelectorVisibility } from "./hooks/useSettingsBeamerSelectorVisibility";

export function GeneralSettingsView() {
  const selectedFulfillmentCenter = useAppSelector(
    selectUsersFulfillmentCenter
  );
  const usersName = useAppSelector(selectUsersProfileName);
  const isAutostoreEmailAddress =
    usersName?.includes("autostoresystem.com") ?? false;
  const areDevCheatsEnabled =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";
  const showServerVersion = isAutostoreEmailAddress || areDevCheatsEnabled;
  const { data: serverVersion } = useGetServerVersionQuery(undefined, {
    skip: !showServerVersion
  });

  const { t } = useTranslation();
  const { palette } = useTheme();
  const { infoToast } = useToast();
  const navigate = useFcNavigate();
  const { setThisWorkstation } = useSetThisWorkstation();
  const [selectedGridId, setSelectedGridId] = useState<string | undefined>(
    undefined
  );

  const isAdmin = useAppSelector(selectUserIsAdmin);
  const workstation = useAppSelector(selectThisWorkstation);

  const selectedGridIdForQuery =
    selectedGridId != null && String(selectedGridId).length > 0
      ? selectedGridId
      : undefined;
  const {
    error: selectedAutostoreGridError,
    refetch: refetchSelectedAutostoreGrid
  } = useGetAutostoreGridQuery(selectedGridIdForQuery ?? skipToken, {
    skip: !selectedFulfillmentCenter || !selectedGridIdForQuery
  });
  const { data: autostoreGrids } = useGetAutostoreGridsQuery(undefined, {
    skip: !selectedFulfillmentCenter
  });

  const fulfillmentCenterId = selectedFulfillmentCenter?.fulfillmentCenterId;

  useEffect(() => {
    if (!fulfillmentCenterId || !autostoreGrids) {
      setSelectedGridId(undefined);
      return;
    }

    const wsGridId = workstation?.autostoreGridId;
    setSelectedGridId((previousGridId) => {
      if (
        previousGridId &&
        autostoreGrids.some((g) => g.autostoreGridId === previousGridId)
      ) {
        return previousGridId;
      }

      if (
        wsGridId &&
        autostoreGrids.some((g) => g.autostoreGridId === wsGridId)
      ) {
        return wsGridId;
      }

      return undefined;
    });
  }, [autostoreGrids, fulfillmentCenterId, workstation?.autostoreGridId]);

  const handleGridSelected = useCallback(
    async (nextGridId: string | undefined) => {
      setSelectedGridId(nextGridId);

      if (workstation?.autostoreGridId === nextGridId) {
        return;
      }

      const hadWorkstation = Boolean(workstation);
      await setThisWorkstation(null, true);
      if (hadWorkstation) {
        infoToast(t("workstation cleared when grid selection changes"));
      }
    },
    [workstation, setThisWorkstation, infoToast, t]
  );

  useSettingsBeamerSelectorVisibility(isAdmin);

  const { enableSideNavSubMenus } = useFlag();

  const isWorkstationConfigurer = useAppSelector(
    selectUserIsWorkstationConfigurer
  );

  const handleCopyFulfillmentCenterId = async () => {
    await copyToClipboard(selectedFulfillmentCenter?.fulfillmentCenterId || "");
    infoToast(t("copy to clipboard"), {
      duration: 2000
    });
  };

  const mainStack = (
    <Stack spacing={settingsSectionGap} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        gap={1.5}
        sx={{ minWidth: 0, columnGap: 2 }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            flex: "0 1 auto",
            minWidth: 0,
            fontWeight: 500,
            letterSpacing: 0
          }}
        >
          {t("general")}
        </Typography>
        <Box
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" }
          }}
        >
          <FcGridInfo />
        </Box>
      </Stack>

      {isWorkstationConfigurer && (
        <>
          <SettingsSectionWrapper title={t("fulfillment center")}>
            <Stack spacing={2}>
              <FulfillmentCenterSelect />
              <Stack direction="column" spacing={0.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={0.5}
                >
                  <Typography variant="body1" component="span">
                    {t("fulfillment center")} ID:{" "}
                    <Box
                      component="span"
                      onClick={handleCopyFulfillmentCenterId}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          void handleCopyFulfillmentCenterId();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textUnderlineOffset: 2
                      }}
                    >
                      {selectedFulfillmentCenter?.fulfillmentCenterId}
                    </Box>
                  </Typography>
                  <IconButton
                    onClick={handleCopyFulfillmentCenterId}
                    aria-label={t("copy to clipboard")}
                    size="small"
                  >
                    <Duplicate20Px style={{ fill: palette.text.secondary }} />
                  </IconButton>
                </Stack>
                <Typography variant="caption" color="textSecondary">
                  {t("selected fulfillment center id description")}
                </Typography>
              </Stack>
            </Stack>
          </SettingsSectionWrapper>

          <Divider sx={{ width: "100%" }} role="presentation" />

          <SettingsSectionWrapper title={t("grid")}>
            <GridSection
              selectedGridId={selectedGridId}
              onGridSelected={handleGridSelected}
            />
          </SettingsSectionWrapper>
          {!!selectedAutostoreGridError && (
            <Alert
              variant="outlined"
              severity="error"
              action={
                <Button onClick={refetchSelectedAutostoreGrid}>
                  {t("retry")}
                </Button>
              }
            >
              {getMessageFromRtkError(selectedAutostoreGridError)}
            </Alert>
          )}

          <Divider sx={{ width: "100%" }} role="presentation" />

          <SettingsSectionWrapper title={t("workstation")}>
            <WorkstationPanel selectedGridId={selectedGridId} />
          </SettingsSectionWrapper>

          <Divider sx={{ width: "100%" }} role="presentation" />
        </>
      )}

      <SettingsSectionWrapper title={t("language")}>
        <LanguageSettings />
      </SettingsSectionWrapper>

      <Divider sx={{ width: "100%" }} role="presentation" />

      <SettingsSectionWrapper title={t("api token")}>
        <Stack gap={1}>
          <Typography variant="body2" color="textSecondary">
            {t("organization api tokens description")}
          </Typography>
          <Button
            variant="text"
            color="primary"
            onClick={() => navigate("/settings/api-tokens")}
            sx={{ justifyContent: "flex-start", width: "fit-content" }}
          >
            {t("create and manage api tokens")}
          </Button>
        </Stack>
      </SettingsSectionWrapper>

      <Divider sx={{ width: "100%" }} role="presentation" />

      <SettingsSectionWrapper title={t("nav.viewname.version")}>
        <Stack component="div" aria-label="version information" gap={2}>
          <Stack direction="row" gap={2} alignItems="baseline" flexWrap="wrap">
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ minWidth: 88 }}
            >
              {t("client version")}
            </Typography>
            <Typography component="span" variant="body1" fontWeight={600}>
              {envConstants.VERSION_TAG || envConstants.VERSION || "Unknown"}
            </Typography>
          </Stack>
          {showServerVersion && (
            <Stack
              direction="row"
              gap={2}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ minWidth: 88 }}
              >
                {t("server version")}
              </Typography>
              <Typography component="span" variant="body1" fontWeight={600}>
                {serverVersion?.tag || serverVersion?.hash || "Unknown"}
              </Typography>
            </Stack>
          )}
        </Stack>
      </SettingsSectionWrapper>
    </Stack>
  );

  if (!enableSideNavSubMenus) {
    return (
      <Box id="general-settings-view-root" sx={settingsPagePaddingSx}>
        {mainStack}
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ display: "block" }}>
      <Box bgcolor={palette.background.secondary} pb="4px" mt={2}>
        <Box
          bgcolor={palette.background.default}
          mx="4px"
          borderRadius="8px"
          sx={settingsPagePaddingSx}
        >
          {mainStack}
        </Box>
      </Box>
    </Container>
  );
}
